import { useEffect, useMemo, useState } from "react";
import Icon from "../../components/Icon";
import { StatusBadge, ClientPendingBadge, TaskPriorityBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import TaskDrawer from "../../components/TaskDrawer";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import { useRefresh } from "../../contexts/RefreshContext";
import { myCurrentTasks, myTaskGroups } from "../../api/myTaskService";
import { fetchAndDownload } from "../../utils/exportCsv";
import { showApiError, showToast } from "../../utils/toast";
import { paginatedRows } from "../../utils/pagination";
import { formatDateShort, daysFromToday, serialNumber, initials, nowIst } from "../../utils/formatters";
import { useAuth } from "../../contexts/AuthContext";

export default function MyTasksPage() {
  const { user } = useAuth();
  const { refreshKey } = useRefresh();
  const [tab, setTab] = useState("today");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [taskGroupId, setTaskGroupId] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [taskType, setTaskType] = useState(""); // "" | "one_off" | "recurring"
  const [roleFilter, setRoleFilter] = useState(""); // "" (both) | "assignee" | "reviewer"
  const [clientPendingOnly, setClientPendingOnly] = useState(false);
  const [noReviewerOnly, setNoReviewerOnly] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const tasks = useApi(
    () => {
      const params = { per_page: 200 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (taskGroupId) params.task_group_id = taskGroupId;
      if (priorityFilter) params.priority = priorityFilter;
      if (taskType) params.task_type = taskType;
      if (roleFilter) params.role = roleFilter;
      if (noReviewerOnly) params.no_reviewer = 1;
      return myCurrentTasks(params);
    },
    [refreshKey, debouncedSearch, taskGroupId, priorityFilter, taskType, roleFilter, noReviewerOnly],
  );
  const groups = useApi(() => myTaskGroups());

  const exportMyTasks = async () => {
    setExporting(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (taskGroupId) params.task_group_id = taskGroupId;
      if (priorityFilter) params.priority = priorityFilter;
      if (taskType) params.task_type = taskType;
      if (roleFilter) params.role = roleFilter;
      if (noReviewerOnly) params.no_reviewer = 1;
      await fetchAndDownload("/my/tasks/export", params, "my_tasks");
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };
  const rows = paginatedRows(tasks.data);
  const groupRows = paginatedRows(groups.data);
  const groupOptions = useMemo(
    () => groupRows.map((g) => ({ id: g.id, label: g.name })),
    [groupRows],
  );

  const buckets = useMemo(() => {
    // "This week" runs from tomorrow through the coming Sunday (the end of the
    // current calendar week), not a rolling today+7. dayjs().day() is 0=Sun…
    // 6=Sat in IST, so days-to-Sunday is 0 on a Sunday (week bucket empty,
    // everything ahead falls into "Later").
    const daysToSunday = (7 - nowIst().day()) % 7;
    const today = rows.filter((t) => daysFromToday(t.due_date) === 0 && t.status !== "completed");
    const overdue = rows.filter((t) => daysFromToday(t.due_date) < 0 && t.status !== "completed");
    const week = rows.filter((t) => {
      const d = daysFromToday(t.due_date);
      return d > 0 && d <= daysToSunday && t.status !== "completed";
    });
    const later = rows.filter((t) => daysFromToday(t.due_date) > daysToSunday && t.status !== "completed");
    return { today, overdue, week, later };
  }, [rows]);

  const list = useMemo(() => {
    const base = buckets[tab] || [];
    return clientPendingOnly ? base.filter((t) => t.is_client_pending) : base;
  }, [buckets, tab, clientPendingOnly]);
  const filterActive = !!debouncedSearch || !!taskGroupId || !!priorityFilter || !!taskType || !!roleFilter || clientPendingOnly || noReviewerOnly;
  const clearFilters = () => {
    setSearch("");
    setTaskGroupId("");
    setPriorityFilter("");
    setTaskType("");
    setRoleFilter("");
    setClientPendingOnly(false);
    setNoReviewerOnly(false);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">My day{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
          <div className="page-sub">
            {buckets.overdue.length} overdue · {buckets.today.length} due today · {buckets.week.length} this week
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={exportMyTasks} disabled={exporting}>
            <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi-label" style={{ color: "var(--danger)" }}>
            <Icon name="alert-triangle" size={12} /> Overdue
          </div>
          <div className="kpi-value">{buckets.overdue.length}</div>
          <div className="kpi-delta down">Resolve first</div>
        </div>
        <div className="kpi">
          <div className="kpi-label"><Icon name="clock" size={12} /> Due today</div>
          <div className="kpi-value">{buckets.today.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label"><Icon name="calendar" size={12} /> This week</div>
          <div className="kpi-value">{buckets.week.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label"><Icon name="layers" size={12} /> Later</div>
          <div className="kpi-value">{buckets.later.length}</div>
        </div>
      </div>

      <div className="spacer-lg" />

      <div className="filter-bar">
        {[
          { id: "today", label: "Today", count: buckets.today.length },
          { id: "overdue", label: "Overdue", count: buckets.overdue.length },
          { id: "week", label: "This week", count: buckets.week.length },
          { id: "later", label: "Later", count: buckets.later.length },
        ].map((b) => (
          <button key={b.id} className={`chip ${tab === b.id ? "active" : ""}`} onClick={() => setTab(b.id)}>
            {b.label} <span className="chip-count">{b.count}</span>
          </button>
        ))}
        <button
          className={`chip ${clientPendingOnly ? "active" : ""}`}
          onClick={() => setClientPendingOnly((v) => !v)}
          title="Show only tasks waiting on the client"
        >
          Client pending
        </button>
        <button
          className={`chip ${noReviewerOnly ? "active" : ""}`}
          onClick={() => setNoReviewerOnly((v) => !v)}
          title="Show only tasks with no reviewer set"
        >
          No reviewer
        </button>
        <SearchableSelect
          value={taskGroupId}
          onChange={(v) => setTaskGroupId(v ?? "")}
          options={groupOptions}
          placeholder="All groups"
          searchPlaceholder="Search groups…"
          width={200}
        />
        <select
          className="select"
          style={{ width: 150 }}
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        >
          <option value="">All priorities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select
          className="select"
          style={{ width: 140 }}
          value={taskType}
          onChange={(e) => setTaskType(e.target.value)}
          title="Filter by recurrence"
        >
          <option value="">All types</option>
          <option value="one_off">One-off</option>
          <option value="recurring">Recurring</option>
        </select>
        <select
          className="select"
          style={{ width: 160 }}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          title="Show tasks by my role"
        >
          <option value="">My role: any</option>
          <option value="assignee">Assigned to me</option>
          <option value="reviewer">Reviewing</option>
        </select>
        <div style={{ position: "relative", flex: 1, maxWidth: 320, marginLeft: "auto" }}>
          <Icon
            name="search"
            size={13}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
          />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search task or business…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {filterActive && (
          <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
            <Icon name="x" size={12} /> Clear
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {tasks.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
          ) : list.length === 0 ? (
            <EmptyState
              icon="check-circle"
              title={filterActive ? "No tasks match the filter" : "All clear"}
              hint={filterActive ? "Try clearing filters." : "Nothing in this bucket right now."}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 50 }} className="t-num">#</th>
                  <th>Task</th>
                  <th>Business</th>
                  <th>Assignee</th>
                  <th>Reviewer</th>
                  <th>Status</th>
                  <th>Due</th>
                  <th style={{ width: 70 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t, i) => {
                  const d = daysFromToday(t.due_date);
                  return (
                    <tr key={t.id} className="clickable" onClick={() => setOpenTask(t.id)}>
                      <td className="t-num t-muted">{serialNumber(null, i)}</td>
                      <td className="t-primary">{t.task_master?.name}</td>
                      <td className="t-muted">
                        {t.business?.business_name}
                        {t.business?.client_id && (
                          <div className="text-xs t-muted">{t.business.client_id}</div>
                        )}
                      </td>
                      <td>
                        {t.employee ? (
                          <div className="row gap-sm">
                            <div className="avatar avatar-sm">{initials(t.employee.name)}</div>
                            <span className="t-muted text-xs">{t.employee.name?.split(" ")[0]}</span>
                          </div>
                        ) : (
                          <span className="badge badge-warning">Unassigned</span>
                        )}
                      </td>
                      <td>
                        {t.reviewer ? (
                          <div className="row gap-sm">
                            <div className="avatar avatar-sm">{initials(t.reviewer.name)}</div>
                            <span className="t-muted text-xs">{t.reviewer.name?.split(" ")[0]}</span>
                          </div>
                        ) : (
                          <span className="t-muted">—</span>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={t.status} />
                        {t.is_client_pending && (
                          <div style={{ marginTop: 4 }}><ClientPendingBadge /></div>
                        )}
                      </td>
                      <td>
                        {d < 0 ? (
                          <span className="badge badge-danger">{Math.abs(d)}d ago</span>
                        ) : d === 0 ? (
                          <span className="badge badge-warning">Today</span>
                        ) : (
                          <span className="t-muted">{formatDateShort(t.due_date)}</span>
                        )}
                        <div style={{ marginTop: 4 }}><TaskPriorityBadge priority={t.priority} /></div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="icon-btn"
                          title="View"
                          onClick={() => setOpenTask(t.id)}
                        >
                          <Icon name="eye" size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <TaskDrawer
        taskId={openTask}
        role="employee"
        onClose={() => setOpenTask(null)}
        onChanged={() => tasks.reload()}
      />
    </>
  );
}
